import { useEffect, useRef, useState } from "react";
import { Lock, Loader2, X } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import { loadSquarePayments } from "@/lib/square";

// In-page Square card entry (Web Payments SDK). Tokenises the card in the browser — card
// numbers never touch our servers — then hands the nonce back for /payments/checkout.
export function SquareCardStep({ skuLabel, priceLabel, onToken, onCancel, busy }) {
  const holder = useRef(null);
  const cardRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | unavailable
  const [err, setErr] = useState("");

  useEffect(() => { holder.current?.closest?.("[data-testid=square-card-step]")?.scrollIntoView({ behavior: "smooth", block: "center" }); }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = (await api.get("/payments/sdk-config")).data || {};
        const sq = await loadSquarePayments({ applicationId: cfg.application_id, locationId: cfg.location_id, mode: cfg.mode });
        if (!sq) { setState("unavailable"); return; }
        const card = await sq.payments.card();
        if (cancelled) return;
        await card.attach(holder.current);
        cardRef.current = card;
        setState("ready");
      } catch (e) {
        if (!cancelled) setState("unavailable");
      }
    })();
    return () => { cancelled = true; try { cardRef.current?.destroy?.(); } catch { /* already gone */ } };
  }, []);

  const pay = async () => {
    setErr("");
    try {
      const res = await cardRef.current.tokenize();
      if (res.status !== "OK" || !res.token) {
        setErr(res.errors?.[0]?.message || "That card couldn't be verified. Check the details and try again.");
        return;
      }
      await onToken(res.token);
    } catch (e) {
      setErr(getErrorMessage(e) || "Something interrupted the payment. Your card has not been charged.");
    }
  };

  return (
    <div className="ss-card p-5 mt-4 border border-mauve/40" data-testid="square-card-step">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Secure checkout</p>
          <p className="font-heading text-2xl text-cocoa mt-1">{skuLabel}</p>
          {priceLabel && <p className="text-sm text-cocoa/65">{priceLabel} · cancel anytime</p>}
        </div>
        <button type="button" onClick={onCancel} aria-label="Close" data-testid="square-card-cancel" className="text-cocoa/50 hover:text-cocoa"><X size={16} /></button>
      </div>

      {state === "unavailable" ? (
        <div className="mt-4 text-sm text-cocoa/75 leading-relaxed" data-testid="square-card-unavailable">
          Secure card payments are almost ready. Nothing has been charged — please check back shortly, or write to us and we'll set you up by hand.
        </div>
      ) : (
        <>
          <div className="mt-4 min-h-[56px]">
            {state === "loading" && <p className="text-sm text-cocoa/55 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Opening secure card form…</p>}
            <div ref={holder} data-testid="square-card-container" />
          </div>
          {err && <p className="mt-2 text-sm text-rose" role="alert" data-testid="square-card-error">{err}</p>}
          <button type="button" onClick={pay} disabled={state !== "ready" || busy} data-testid="square-card-pay" className="mt-4 ss-pill ss-pill-primary h-11 w-full sm:w-auto disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} {busy ? "Confirming…" : `Start ${skuLabel}`}
          </button>
          <p className="mt-2 text-[11px] text-cocoa/50">Processed by Square. Your card details go straight to Square and are never stored by SugarSense.</p>
        </>
      )}
    </div>
  );
}
